import { Trans } from "@lingui/react/macro"
import { Alert, Button, Stack, Text } from "@mantine/core"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * The failure banner shown above the ask composer — one of five states
 * (out-of-credits / unavailable / warming / no-workspace / generic). Pure
 * presentation, shared by the Ask page and the ask-anywhere overlay so the copy
 * and the retry affordance stay in one place. `out-of-credits` has no retry (a
 * top-up is required first); every other state offers "Try again". The tone is a
 * semantic palette (`danger` / `warning`), never a literal hue (theming rule #3).
 */
export const AskErrorBanner = ({ kind, onRetry }: IProps): React.JSX.Element => {
  const { title, body, tone } = ((): { title: React.ReactNode; body: React.ReactNode; tone: string } => {
    switch (kind) {
      case "out-of-credits":
        return {
          tone: "danger",
          title: <Trans>You're out of credits</Trans>,
          body: <Trans>This workspace has run out of credits, so new questions are paused. Top up to continue.</Trans>,
        }
      case "unavailable":
        return {
          tone: "danger",
          title: <Trans>Search is temporarily unavailable</Trans>,
          body: (
            <Trans>We couldn't reach the engine that reads your meetings. This is usually brief — try again.</Trans>
          ),
        }
      case "warming":
        return {
          tone: "warning",
          title: <Trans>Still preparing your meetings</Trans>,
          body: <Trans>This is taking longer than usual to warm up. Give it a moment, then try again.</Trans>,
        }
      case "no-workspace":
        return {
          tone: "warning",
          title: <Trans>No workspace selected</Trans>,
          body: <Trans>Your account isn't in an active workspace, so there are no meetings to search yet.</Trans>,
        }
      case "generic":
        return {
          tone: "danger",
          title: <Trans>Something went wrong</Trans>,
          body: <Trans>Your question couldn't be answered just now. Try again.</Trans>,
        }
    }
  })()

  return (
    <Alert variant="light" color={tone} title={title} className={classes.errorBanner}>
      <Stack gap="xs">
        <Text size="sm">{body}</Text>
        {kind !== "out-of-credits" ? (
          <Button variant="light" color={tone} size="xs" onClick={onRetry} className={classes.retry}>
            <Trans>Try again</Trans>
          </Button>
        ) : null}
      </Stack>
    </Alert>
  )
}
